/**
 * @module immersive/QuiverDrawer
 * @description The Quiver (vision §8): the multi-cause cart in Send Love Live
 * language. Per-cause arrows (derived from the amount, never stored twice),
 * per-cause subtotal, one US$ grand total, one checkout through Neon.
 * State is GivingContext's cart; this is only its stage-themed view.
 */
import { useEffect, useRef, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { availableArrows, breakdown, groupArrows, type LegendArrow } from "../domain/arrows";
import type { Cause, CartLine } from "../domain/types";
import { formatUsd } from "../lib/format";
import { Icon } from "../components/Icons";
import { ArrowFace } from "./ArrowFace";
import { useStage } from "./StageContext";

/** Slide-out Quiver panel. */
export function QuiverDrawer(): JSX.Element {
    const { cartOpen, setCartOpen, lines, totalUsd, causeById, dispatchCart } = useGiving();
    const { rebelPreview } = useStage();
    const panel = useRef<HTMLDialogElement>(null);
    const arrows: LegendArrow[] = availableArrows(rebelPreview);

    useEffect(() => {
        const el: HTMLDialogElement | null = panel.current;
        if (el === null) return;
        if (cartOpen && !el.open) el.showModal();
        if (!cartOpen && el.open) el.close();
    }, [cartOpen]);

    return (
        <dialog ref={panel} className="quiver" onClose={() => setCartOpen(false)} onClick={(e) => { if (e.target === panel.current) setCartOpen(false); }} aria-labelledby="quiver-title">
            <div className="quiver__inner">
                <header className="quiver__head">
                    <h2 id="quiver-title">Your Quiver</h2>
                    <button type="button" className="icon-btn" onClick={() => setCartOpen(false)} aria-label="Close your Quiver"><Icon name="close" /></button>
                </header>
                <p className="quiver__helper">You give in US dollars. Legends are named after the Jamaican note or coin with the same number.</p>

                {lines.length === 0 ? (
                    <p className="quiver__empty">Your Quiver is empty. Open a stage and shoot a Legend Arrow.</p>
                ) : (
                    <ul className="quiver__lines">
                        {lines.map((l: CartLine) => {
                            const cause: Cause | undefined = causeById(l.causeId);
                            if (cause === undefined) return null;
                            return (
                                <li key={l.causeId} className="quiver__line">
                                    <div className="quiver__line-head">
                                        <img src={cause.image.src} alt="" />
                                        <div>
                                            <p className="quiver__partner">{cause.partner}</p>
                                            <p className="quiver__title">{cause.title}</p>
                                        </div>
                                        <strong className="quiver__subtotal">{formatUsd(l.amountUsd)}</strong>
                                    </div>
                                    <div className="quiver__arrows">
                                        {groupArrows(breakdown(l.amountUsd, arrows)).map(([a, n]) => <ArrowFace key={a.id} arrow={a} count={n} />)}
                                    </div>
                                    <button type="button" className="quiver__remove" onClick={() => dispatchCart({ type: "remove", causeId: l.causeId })}>Remove</button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {lines.length > 0 && (
                    <footer className="quiver__foot">
                        <div className="quiver__total"><span>{lines.length} stage{lines.length === 1 ? "" : "s"}</span><strong>{formatUsd(totalUsd)}</strong></div>
                        <button type="button" className="btn btn--gold btn--lg btn--block" onClick={() => { setCartOpen(false); navigate("checkout"); }}>
                            <Icon name="lock" /> Check out once · {formatUsd(totalUsd)}
                        </button>
                        <p className="quiver__fine">One payment through Neon's secure checkout. Tax-deductible gifts to The American Friends of Jamaica, designated to each cause.</p>
                    </footer>
                )}
            </div>
        </dialog>
    );
}
